import { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

export default function ActivityPopover({ trigger, initialValues, initialChildNames, familyChildren, defaultDate, onSave, onDelete, open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const [title, setTitle] = useState(initialValues?.title || '');
  const [date, setDate] = useState(initialValues?.date || defaultDate);
  const [startTime, setStartTime] = useState(initialValues?.start_time || '09:00');
  const [duration, setDuration] = useState(initialValues?.duration_minutes || 30);
  const [selectedChildren, setSelectedChildren] = useState(initialValues?.child_names || initialChildNames || familyChildren.map(c => c.name));
  const [notes, setNotes] = useState(initialValues?.notes || '');

  const toggleChild = (name) => {
    setSelectedChildren(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const handleSave = async () => {
    if (!title.trim() || selectedChildren.length === 0) return;
    try {
      await onSave({ title: title.trim(), date, start_time: startTime, duration_minutes: Number(duration), child_names: selectedChildren, notes: notes.trim() || null });
      setOpen(false);
    } catch {
      // onSave already surfaces a toast; keep the popover open so the user can retry.
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger || <span />}</PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" data-testid="activity-popover">
        <div>
          <Label htmlFor="activity-title">Title</Label>
          <Input id="activity-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Swimming lesson" data-testid="activity-title-input" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="activity-date">Date</Label>
            <Input id="activity-date" type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="activity-date-input" />
          </div>
          <div>
            <Label htmlFor="activity-time">Start time</Label>
            <Input id="activity-time" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="activity-time-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="activity-duration">Duration (min)</Label>
          <Input id="activity-duration" type="number" min={5} step={5} value={duration} onChange={e => setDuration(e.target.value)} data-testid="activity-duration-input" />
        </div>
        <div>
          <Label>Who's this for?</Label>
          <div className="space-y-1 mt-1">
            {familyChildren.map(c => (
              <label key={c.name} className="flex items-center gap-2 text-sm">
                <Checkbox checked={selectedChildren.includes(c.name)} onCheckedChange={() => toggleChild(c.name)} data-testid={`activity-child-${c.name}`} />
                {c.name}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label htmlFor="activity-notes">Notes (optional)</Label>
          <Textarea id="activity-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={2} data-testid="activity-notes-input" />
        </div>
        <div className="flex justify-between pt-1">
          {initialValues && (
            <Button variant="ghost" className="text-slate-500 hover:text-slate-800" onClick={async () => { try { await onDelete(); setOpen(false); } catch { /* NestPage already toasts the error */ } }} data-testid="activity-delete-btn">
              Delete
            </Button>
          )}
          <Button onClick={handleSave} className="ml-auto bg-fern hover:bg-fern-dark text-white" data-testid="activity-save-btn">
            {initialValues ? 'Save' : 'Add'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
